import * as THREE from 'three';

export interface AudioManifest {
  /** Loop name → loop points (s) inside its file (scripts/fetch_sounds.py). */
  loops: Record<string, { start: number; end: number }>;
  /** One-shot name → [start s, duration s] in sfx.mp3. */
  sprite: Record<string, [number, number]>;
}

const BASE = `${import.meta.env.BASE_URL}audio/`;
const STORE = 'perpcity.audio';

/** A looping buffer source and the gain it plays through (started once, runs until stopped). */
export interface LoopVoice {
  src: AudioBufferSourceNode;
  gain: GainNode;
}

/**
 * The Web Audio graph (DECISIONS "Audio"). Sounds go to one of three buses:
 * - outdoor: the city beds and the street's sources, through a low-pass that closes indoors (walls
 *   muffle the street);
 * - direct: the room tone, the player's own car and footsteps;
 * - reverb sends: a street slap-back and a small-room tail (impulse responses generated here), their
 *   levels set by where the listener is.
 * Everything meets in the master gain (volume, mute) and a gentle compressor. The context starts on the
 * first click or key press (browsers block audio until then); the sounds load then, and are dropped
 * silently if they fail.
 */
export class AudioEngine {
  ctx: AudioContext | null = null;
  master!: GainNode;
  /** Outdoor sources connect here. */
  outdoor!: GainNode;
  outdoorFilter!: BiquadFilterNode;
  /** Sources heard as they are (room tone, the player's car and steps). */
  direct!: GainNode;
  /** Reverb sends (their gains are the wet levels). */
  streetSend!: GainNode;
  hallSend!: GainNode;
  private readonly loops = new Map<string, AudioBuffer>();
  private sfx: AudioBuffer | null = null;
  private manifest: AudioManifest | null = null;
  noise: AudioBuffer | null = null;
  ready = false;
  private readonly readyCbs: (() => void)[] = [];
  private _volume = 0.8;
  private _muted = false;
  private hidden = false;
  private ducked = 1;

  constructor(readonly enabled = true) {
    try {
      const s = JSON.parse(localStorage.getItem(STORE) ?? '{}') as { volume?: number; muted?: boolean };
      if (typeof s.volume === 'number') this._volume = Math.max(0, Math.min(1, s.volume));
      if (typeof s.muted === 'boolean') this._muted = s.muted;
    } catch {
      /* storage unavailable: defaults */
    }
    if (!enabled) return;
    const unlock = () => this.unlock();
    window.addEventListener('pointerdown', unlock, { capture: true });
    window.addEventListener('keydown', unlock, { capture: true });
    document.addEventListener('visibilitychange', () => {
      this.hidden = document.hidden;
      if (!this.ctx) return;
      if (this.hidden) void this.ctx.suspend();
      else void this.ctx.resume();
    });
  }

  get volume(): number {
    return this._volume;
  }

  get muted(): boolean {
    return this._muted;
  }

  setVolume(v: number, muted = this._muted): void {
    this._volume = Math.max(0, Math.min(1, v));
    this._muted = muted;
    if (this.ctx) this.master.gain.setTargetAtTime(this.level, this.ctx.currentTime, 0.05);
    try {
      localStorage.setItem(STORE, JSON.stringify({ volume: this._volume, muted: this._muted }));
    } catch {
      /* not saved */
    }
  }

  /** Perceived loudness follows the slider: square law. */
  private get level(): number {
    return this._muted ? 0 : this._volume * this._volume * this.ducked;
  }

  /** Turn everything down to `k` of the set volume (the pause menu), or back up with 1. */
  duck(k: number): void {
    if (k === this.ducked) return;
    this.ducked = k;
    if (this.ctx) this.master.gain.setTargetAtTime(this.level, this.ctx.currentTime, 0.15);
  }

  onReady(cb: () => void): void {
    if (this.ready) cb();
    else this.readyCbs.push(cb);
  }

  /** Create (or resume) the context on a user gesture and load the sounds. */
  unlock(): void {
    if (!this.enabled) return;
    if (this.ctx) {
      if (this.ctx.state === 'suspended' && !this.hidden) void this.ctx.resume();
      return;
    }
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx({ latencyHint: 'interactive' });
    this.ctx = ctx;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.knee.value = 12;
    comp.ratio.value = 3;
    comp.attack.value = 0.008;
    comp.release.value = 0.25;
    comp.connect(ctx.destination);
    this.master = ctx.createGain();
    this.master.gain.value = this.level;
    this.master.connect(comp);
    this.direct = ctx.createGain();
    this.direct.connect(this.master);
    this.outdoorFilter = ctx.createBiquadFilter();
    this.outdoorFilter.type = 'lowpass';
    this.outdoorFilter.frequency.value = 20000;
    this.outdoorFilter.Q.value = 0.5;
    this.outdoorFilter.connect(this.master);
    this.outdoor = ctx.createGain();
    this.outdoor.connect(this.outdoorFilter);
    this.streetSend = ctx.createGain();
    this.hallSend = ctx.createGain();
    this.streetSend.gain.value = 0;
    this.hallSend.gain.value = 0;
    for (const [send, ir] of [
      [this.streetSend, impulse(ctx, 1.9, 0.035, 5200)],
      [this.hallSend, impulse(ctx, 0.75, 0.008, 7000)],
    ] as const) {
      const conv = ctx.createConvolver();
      conv.buffer = ir;
      send.connect(conv);
      conv.connect(this.master);
    }
    this.noise = noiseBuffer(ctx, 4);
    void this.load().catch((e) => console.warn('audio: sounds failed to load', e));
  }

  private async load(): Promise<void> {
    const ctx = this.ctx!;
    const fetchBuf = async (name: string) => ctx.decodeAudioData(await (await fetch(`${BASE}${name}.mp3`)).arrayBuffer());
    const manifest = (await (await fetch(`${BASE}manifest.json`)).json()) as AudioManifest;
    this.manifest = manifest;
    const names = Object.keys(manifest.loops);
    const bufs = await Promise.all([...names.map(fetchBuf), fetchBuf('sfx')]);
    names.forEach((n, i) => this.loops.set(n, bufs[i]));
    this.sfx = bufs[names.length];
    this.ready = true;
    for (const cb of this.readyCbs.splice(0)) cb();
  }

  /** Start a loop (at a random point) through a new gain into `dest`. */
  loop(name: string, dest: AudioNode, gain = 0): LoopVoice | null {
    const ctx = this.ctx;
    const buf = this.loops.get(name);
    const pts = this.manifest?.loops[name];
    if (!ctx || !buf || !pts) return null;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.loopStart = pts.start;
    src.loopEnd = pts.end;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(g);
    g.connect(dest);
    src.start(0, pts.start + Math.random() * (pts.end - pts.start));
    return { src, gain: g };
  }

  /** A looping noise source (for tyre roar and wind), started at a random point. */
  noiseSource(): AudioBufferSourceNode | null {
    if (!this.ctx || !this.noise) return null;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    src.start(0, Math.random() * this.noise.duration);
    return src;
  }

  /** Play a one-shot from the sprite into `dest`. Returns its source (null if not loaded). */
  shot(name: string, dest: AudioNode, gain = 1, rate = 1, when = 0): AudioBufferSourceNode | null {
    const ctx = this.ctx;
    const clip = this.manifest?.sprite[name];
    if (!ctx || !this.sfx || !clip) return null;
    const src = ctx.createBufferSource();
    src.buffer = this.sfx;
    src.playbackRate.value = rate;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(g);
    g.connect(dest);
    src.start(Math.max(ctx.currentTime, when), clip[0], clip[1] + 0.02);
    src.onended = () => g.disconnect();
    return src;
  }

  /** A soft UI click: a short, quickly decaying sine blip. */
  click(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(1500, t);
    o.frequency.exponentialRampToValueAtTime(700, t + 0.04);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.12, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
    o.connect(g);
    g.connect(this.direct);
    o.start(t);
    o.stop(t + 0.07);
    o.onended = () => g.disconnect();
  }

  /** Length (s) of a sprite clip (0 if unknown). */
  duration(name: string): number {
    return this.manifest?.sprite[name]?.[1] ?? 0;
  }

  /** Whether the sprite has a clip of this name. */
  has(name: string): boolean {
    return !!this.manifest?.sprite[name];
  }

  /** A 3D source: HRTF panning, inverse distance roll-off from `ref` metres. */
  panner(ref = 6, rolloff = 1): PannerNode | null {
    if (!this.ctx) return null;
    const p = this.ctx.createPanner();
    p.panningModel = 'HRTF';
    p.distanceModel = 'inverse';
    p.refDistance = ref;
    p.rolloffFactor = rolloff;
    p.maxDistance = 2000;
    return p;
  }

  /** Put the listener at the camera. */
  setListener(cam: THREE.Camera): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const l = ctx.listener;
    const e = cam.matrixWorld.elements;
    const [px, py, pz] = [e[12], e[13], e[14]];
    const [fx, fy, fz] = [-e[8], -e[9], -e[10]];
    const [ux, uy, uz] = [e[4], e[5], e[6]];
    if (l.positionX) {
      const t = ctx.currentTime;
      l.positionX.setValueAtTime(px, t);
      l.positionY.setValueAtTime(py, t);
      l.positionZ.setValueAtTime(pz, t);
      l.forwardX.setValueAtTime(fx, t);
      l.forwardY.setValueAtTime(fy, t);
      l.forwardZ.setValueAtTime(fz, t);
      l.upX.setValueAtTime(ux, t);
      l.upY.setValueAtTime(uy, t);
      l.upZ.setValueAtTime(uz, t);
    } else {
      l.setPosition(px, py, pz);
      l.setOrientation(fx, fy, fz, ux, uy, uz);
    }
  }

  /** Record the mix for `seconds` (debugging and tests): resolves to a WebM/Opus blob. */
  async record(seconds: number): Promise<Blob | null> {
    const ctx = this.ctx;
    if (!ctx || typeof MediaRecorder === 'undefined') return null;
    const dest = ctx.createMediaStreamDestination();
    this.master.connect(dest);
    const rec = new MediaRecorder(dest.stream);
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => chunks.push(e.data);
    const done = new Promise<Blob>((res) => (rec.onstop = () => res(new Blob(chunks, { type: rec.mimeType }))));
    rec.start();
    await new Promise((r) => setTimeout(r, seconds * 1000));
    rec.stop();
    const blob = await done;
    this.master.disconnect(dest);
    return blob;
  }
}

/** Set an AudioParam smoothly toward `v` (time constant `tau` s). */
export function ease(p: AudioParam, v: number, ctx: BaseAudioContext, tau = 0.08): void {
  p.setTargetAtTime(v, ctx.currentTime, tau);
}

/**
 * Stereo pink noise (-3 dB/octave, Paul Kellet's filter), `seconds` long: the base for wind and tyre
 * roar, which are darker than white noise. Looped: long enough not to be heard repeating.
 */
function noiseBuffer(ctx: BaseAudioContext, seconds: number): AudioBuffer {
  const n = Math.floor(ctx.sampleRate * seconds);
  const x = Math.floor(ctx.sampleRate * 0.05);
  const b = ctx.createBuffer(2, n, ctx.sampleRate);
  let s = 22222;
  const rand = () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296) * 2 - 1;
  const tmp = new Float32Array(n + x);
  for (let c = 0; c < 2; c++) {
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < n + x; i++) {
      const w = rand();
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.969 * b2 + w * 0.153852;
      b3 = 0.8665 * b3 + w * 0.3104856;
      b4 = 0.55 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.016898;
      tmp[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
      b6 = w * 0.115926;
    }
    // Seamless loop: the samples past the end fade out over the head (as scripts/fetch_sounds.py does).
    const d = b.getChannelData(c);
    d.set(tmp.subarray(0, n));
    for (let i = 0; i < x; i++) {
      const t = i / x;
      d[i] = tmp[i] * Math.sqrt(t) + tmp[n + i] * Math.sqrt(1 - t);
    }
  }
  return b;
}

/**
 * A reverb impulse response: a few early reflections, then exponentially decaying stereo noise that
 * darkens as it decays (air and soft surfaces absorb the highs first). `decay` is the RT60 in seconds.
 */
function impulse(ctx: BaseAudioContext, decay: number, preDelay: number, brightHz: number): AudioBuffer {
  const rate = ctx.sampleRate;
  const n = Math.floor(rate * (decay + preDelay + 0.1));
  const b = ctx.createBuffer(2, n, rate);
  let s = 4242;
  const rand = () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296) * 2 - 1;
  const pre = Math.floor(preDelay * rate);
  for (let c = 0; c < 2; c++) {
    const d = b.getChannelData(c);
    let lp = 0;
    for (let i = pre; i < n; i++) {
      const t = (i - pre) / rate;
      const env = Math.pow(10, (-3 * t) / decay);
      // One-pole low-pass whose cutoff falls over the tail.
      const fc = brightHz * Math.exp(-t * 2.2) + 400;
      const a = 1 - Math.exp((-2 * Math.PI * fc) / rate);
      lp += (rand() - lp) * a;
      d[i] = lp * env;
    }
    // Early reflections (walls across the street, the floor).
    for (const [ms, g] of [
      [11, 0.5],
      [23, 0.35],
      [41, 0.25],
      [67, 0.15],
    ]) {
      const i = pre + Math.floor(((ms * (c ? 1.13 : 1)) / 1000) * rate);
      if (i < n) d[i] += g * (c ? -1 : 1);
    }
  }
  // Normalise the energy so the send levels mean the same for both rooms.
  let e = 0;
  for (let c = 0; c < 2; c++) for (const v of b.getChannelData(c)) e += v * v;
  const k = 1 / Math.sqrt(e / 2);
  for (let c = 0; c < 2; c++) {
    const d = b.getChannelData(c);
    for (let i = 0; i < n; i++) d[i] *= k * 0.5;
  }
  return b;
}
